import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { getBacktest } from '../api';

const POLL_INTERVAL = 30_000; // 30 seconds

function timeAgo(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const TYPE_COLOR = {
  success: 'var(--teal)',
  error: '#ef4444',
  warning: '#f97316',
  info: 'var(--blue)',
};

const TYPE_ICON = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'i',
};

export default function NotificationCenter({ onBacktestLoaded }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [panelPos, setPanelPos] = useState({ top: 40, left: 0, width: 340 });
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  // Ids we've already surfaced. null = first fetch not done yet, so we don't
  // auto-pop the panel for notifications that already existed on page load.
  const seenIdsRef = useRef(null);
  // Ids of background-run notifications we've already popped for on completion,
  // so an in-place "running → completed/failed" flip re-pops the panel exactly once.
  const completedIdsRef = useRef(new Set());

  // Position the panel under the bell (shared by manual click and auto-open).
  const positionPanel = useCallback(() => {
    if (!bellRef.current) return;
    const rect = bellRef.current.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 16);
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setPanelPos({ top: rect.bottom + 6, left, width });
  }, []);

  const openPanel = useCallback(() => {
    positionPanel();
    setOpen(true);
  }, [positionPanel]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/auth/notifications/');
      const incoming = res.data.notifications || [];
      setNotifications(incoming);
      setUnreadCount(res.data.unread_count);

      // A background run that just flipped to a terminal state was updated in
      // place (same id), so it won't look "new" — track it separately and pop once.
      const hasFinishedRun = incoming.some((n) => {
        const st = n.data?.status;
        if ((st === 'completed' || st === 'failed') && n.data?.run_kind && !n.read
            && !completedIdsRef.current.has(n.id)) {
          completedIdsRef.current.add(n.id);
          return true;
        }
        return false;
      });

      if (seenIdsRef.current === null) {
        // First load: remember what already existed; don't auto-open for it.
        seenIdsRef.current = new Set(incoming.map((n) => n.id));
        // Pre-seed completed runs so we don't pop for ones finished before load.
        incoming.forEach((n) => {
          const st = n.data?.status;
          if (st === 'completed' || st === 'failed') completedIdsRef.current.add(n.id);
        });
      } else {
        // A new, still-unread notification arrived since last poll — pop the
        // panel open so it's clearly noticed (as though the bell was clicked).
        const hasNewUnread = incoming.some((n) => !seenIdsRef.current.has(n.id) && !n.read);
        incoming.forEach((n) => seenIdsRef.current.add(n.id));
        if (hasNewUnread || hasFinishedRun) openPanel();
      }
    } catch {
      // silently ignore (user may not be authenticated yet)
    }
  }, [openPanel]);

  // Poll on mount and every POLL_INTERVAL ms
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.patch(`/auth/notifications/${id}/read/`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.post('/auth/notifications/mark-all-read/');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/auth/notifications/${id}/`);
      const removed = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (removed && !removed.read) setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const handleNotificationClick = useCallback(async (n) => {
    if (!n.read) markRead(n.id);
    if (n.data?.message_thread_id) {
      setOpen(false);
      navigate(`/messages/${n.data.message_thread_id}`);
      return;
    }
    if (n.data?.backtest_id && onBacktestLoaded) {
      try {
        const res = await getBacktest(n.data.backtest_id);
        onBacktestLoaded(res.data);
        setOpen(false);
      } catch {}
      return;
    }
    // Analyst→builder generation finished: send them to the saved strategy (or
    // wherever the notification's link points).
    if (n.data?.strategy_id || n.data?.link) {
      setOpen(false);
      navigate(n.data.link || '/strategies');
    }
  }, [markRead, onBacktestLoaded, navigate]);

  const handleBellClick = () => {
    setOpen((v) => {
      const next = !v;
      if (next) positionPanel();
      return next;
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        ref={bellRef}
        onClick={handleBellClick}
        title="Notifications"
        style={{
          position: 'relative',
          background: open ? 'var(--card)' : 'transparent',
          border: '1px solid ' + (open ? 'var(--border)' : 'transparent'),
          borderRadius: 6,
          width: 34,
          height: 34,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: 'var(--text)',
          flexShrink: 0,
        }}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: '#ef4444',
            color: '#fff',
            borderRadius: '50%',
            width: 16,
            height: 16,
            fontSize: 9,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            pointerEvents: 'none',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: panelPos.top,
            left: panelPos.left,
            width: panelPos.width,
            maxHeight: 480,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
              Notifications
              {unreadCount > 0 && (
                <span style={{
                  marginLeft: 8,
                  background: '#ef4444',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '1px 7px',
                  fontSize: 10,
                  fontWeight: 700,
                }}>{unreadCount}</span>
              )}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--teal)',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '32px 16px',
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: 13,
              }}>
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => {
                const isMessage = !!n.data?.message_thread_id;
                const isNavigable = isMessage || !!n.data?.backtest_id || !!n.data?.strategy_id || !!n.data?.link;
                const isClickable = isNavigable || !n.read;
                const isHovered = hoveredId === n.id;
                return (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  onMouseEnter={() => isClickable && setHoveredId(n.id)}
                  onMouseLeave={() => setHoveredId((id) => (id === n.id ? null : id))}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 14px',
                    borderBottom: '1px solid var(--border)',
                    borderLeft: isMessage ? '3px solid #8b5cf6' : '3px solid transparent',
                    cursor: isClickable ? 'pointer' : 'default',
                    background: isHovered && isClickable
                      ? 'rgba(45,212,191,0.08)'
                      : (n.read ? 'transparent' : 'rgba(255,255,255,0.03)'),
                    transition: 'background 0.15s',
                  }}
                >
                  {/* Type icon */}
                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: isMessage ? '#8b5cf6' : (TYPE_COLOR[n.type] || 'var(--blue)'),
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMessage ? 12 : 11,
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: 1,
                  }}>
                    {isMessage ? '💬' : (TYPE_ICON[n.type] || 'i')}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      {isMessage && (
                        <span style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: '#8b5cf6',
                          border: '1px solid #8b5cf6',
                          borderRadius: 4,
                          padding: '1px 5px',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          flexShrink: 0,
                          marginTop: 1,
                        }}>
                          Message
                        </span>
                      )}
                      <span style={{
                        fontWeight: n.read ? 500 : 700,
                        fontSize: 12,
                        color: 'var(--text)',
                        flex: 1,
                        wordBreak: 'break-word',
                      }}>
                        {n.title}
                      </span>
                      {!n.read && (
                        <span style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: 'var(--teal)',
                          flexShrink: 0,
                          marginTop: 4,
                        }} />
                      )}
                    </div>
                    {n.message && (
                      <div style={{
                        fontSize: 11,
                        color: 'var(--muted)',
                        marginTop: 2,
                        wordBreak: 'break-word',
                      }}>
                        {n.message}
                      </div>
                    )}
                    {n.data?.status === 'running' && n.data?.progress && (
                      <div style={{
                        marginTop: 6,
                        height: 5,
                        borderRadius: 999,
                        background: 'var(--border)',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%',
                          width: n.data.progress.total > 0
                            ? `${Math.min(100, Math.round((n.data.progress.current / n.data.progress.total) * 100))}%`
                            : '15%',
                          background: 'var(--teal)',
                          borderRadius: 999,
                          transition: 'width 0.4s ease',
                        }} />
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, opacity: 0.7 }}>
                      {timeAgo(n.created_at)}
                    </div>
                  </div>

                  {/* Navigates-somewhere affordance */}
                  {isNavigable && (
                    <span style={{
                      color: isHovered ? 'var(--teal)' : 'var(--muted)',
                      fontSize: 14,
                      flexShrink: 0,
                      alignSelf: 'center',
                      transition: 'color 0.15s',
                    }}>
                      ›
                    </span>
                  )}

                  {/* Delete */}
                  <button
                    onClick={(e) => remove(n.id, e)}
                    title="Dismiss"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--muted)',
                      fontSize: 14,
                      padding: '0 2px',
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                      marginTop: 1,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
                );
              })
            )}
          </div>
          {/* Footer */}
          <div style={{
            borderTop: '1px solid var(--border)',
            padding: '8px 14px',
            flexShrink: 0,
          }}>
            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--teal)', fontSize: 12, fontWeight: 600, padding: 0,
              }}
            >
              View all notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
